import React, { useState, useEffect, useMemo } from 'react';
import { uploadFile } from './files';
import { supabase } from './supabase';
import { AddressField } from './ui/AddressField';
import { Plus, Trash2, Save, ShoppingBag, X, Search, ChevronDown, Circle, Store, FileText, Frame } from 'lucide-react';
// Componentes de uiarc.dev (src/ui/arc) + primitivas propias
import { Dialog, DialogContent } from './ui/arc/dialog';
import { Input } from './ui/arc/input';
import { Textarea } from './ui/arc/textarea';
import { Checkbox } from './ui/arc/checkbox';
import { Button } from './ui/arc/button';
import { Badge } from './ui/arc/badge';
import SegmentedControl from './ui/arc/segmented-control';
import { Thumb } from './ui/Thumb';
import { cn } from './ui/Table';
import { zoneFromComponents, ZONE_PRICE, ZONE_LABEL, ShippingZone } from './shippingZones';
// Ojo: NewOrder.css define estilos GLOBALES (.modal-overlay, .modal-content, .form-group…) que
// usan otras pantallas (Costos, Consignación, Círculo Rojo…). El popup ya no los usa, pero el
// import se queda hasta mudar esas reglas a un CSS global.
import './NewOrder.css';
import { resolveTemplateName } from './whatsappTemplates';
import { logActivity } from './logger';

function computeOrderDiff(before: any, after: any): Record<string, { before: any; after: any }> {
    const fields = ['contact_name', 'contact_phone', 'discount', 'delivery_type', 'delivery_address', 'delivery_unit', 'note', 'total_price'];
    const diff: Record<string, { before: any; after: any }> = {};
    for (const f of fields) {
        const b = before?.[f] ?? null;
        const a = after?.[f] ?? null;
        if (JSON.stringify(b) !== JSON.stringify(a)) {
            diff[f] = { before: b, after: a };
        }
    }
    const beforeItems = (before?.products || []).map((p: any) => ({ name: p.name, price: Number(p.price) || 0, quantity: Number(p.quantity) || 0, tn_variant_id: p.tn_variant_id }));
    const afterItems = (after?.products || []).map((p: any) => ({ name: p.name, price: Number(p.price) || 0, quantity: Number(p.quantity) || 0, tn_variant_id: p.tn_variant_id }));
    if (JSON.stringify(beforeItems) !== JSON.stringify(afterItems)) {
        diff.products = { before: beforeItems, after: afterItems };
    }
    return diff;
}

// Normalize phone number to international format for WhatsApp API
function normalizePhone(phone: string): string {
    // Strip everything except digits
    let digits = phone.replace(/\D/g, '');
    if (!digits) return '';

    // Handle Argentine numbers
    if (digits.startsWith('0')) {
        // 011... -> 5411...
        digits = '54' + digits.slice(1);
    }
    if (digits.startsWith('15')) {
        // 15XXXX -> 54911XXXX
        digits = '5491' + digits.slice(0);
    }
    if (digits.startsWith('54') && !digits.startsWith('549') && digits.length >= 12) {
        // 5411... -> 549 11...
        digits = '549' + digits.slice(2);
    }
    // If just 10-11 digits starting with 11,etc. assume AR mobile
    if (digits.length >= 10 && digits.length <= 11 && !digits.startsWith('54')) {
        digits = '549' + digits;
    }
    return digits;
}

function isValidPhone(phone: string): boolean {
    const digits = normalizePhone(phone);
    return digits.length >= 12 && digits.length <= 15;
}

async function sendOrderConfirmationWhatsApp(orderId: number | string, formData: any, paymentStatus?: string, isCash?: boolean) {
    if (!formData.contact_phone || formData.source === 'mercadolibre' || !isValidPhone(formData.contact_phone)) return;

    // Determine the correct template based on payment + shipping logic
    let templateToUse: string | null = null;
    const isPickup = formData.delivery_type === 'retiro';
    if (isCash && !isPickup) {
        templateToUse = 'cash_shipping_decision'; // efectivo + envío → template con botones
    } else if (isCash) {
        templateToUse = 'cash_order_confirmation';
    } else if (paymentStatus === 'paid') {
        templateToUse = 'order_confirmation_util';
    }

    // Only send if we evaluated a valid template
    if (!templateToUse) return;

    templateToUse = await resolveTemplateName(templateToUse);

    const phone = normalizePhone(formData.contact_phone);
    const firstName = (formData.contact_name || '').split(' ')[0] || 'Cliente';
    const orderNum = `K-${orderId}`;

    try {
        const buttons = templateToUse === 'cash_shipping_decision'
            ? [
                { index: 0, payload: `retira_${orderId}` },
                { index: 1, payload: `envio_${orderId}` },
            ]
            : undefined;

        const { data: wpResult, error: wpError } = await supabase.functions.invoke('whatsapp-proxy', {
            body: {
                phone,
                templateName: templateToUse,
                language: 'es_AR',
                variables: [
                    { value: firstName },
                    { value: orderNum }
                ],
                ...(buttons ? { buttons } : {}),
            }
        });
        const errorMsg = wpError?.message || wpResult?.error || null;
        const finalError = wpResult?.details && errorMsg ? `${errorMsg} | Meta JSON: ${JSON.stringify(wpResult.details)}` : errorMsg;

        await supabase.from('whatsapp_logs').insert({
            direction: 'outgoing',
            phone,
            contact_name: formData.contact_name,
            template_name: templateToUse,
            status: (wpError || !wpResult?.success) ? 'failed' : 'sent',
            error_message: finalError,
            order_id: orderNum,
            meta_message_id: wpResult?.data?.messages?.[0]?.id || null
        });
    } catch (e: any) {
        console.error('WA confirmation error:', e);
        // No dejar fallar silencioso: dejar rastro en whatsapp_logs aunque no haya meta_message_id.
        try {
            await supabase.from('whatsapp_logs').insert({
                direction: 'outgoing',
                phone,
                contact_name: formData.contact_name,
                template_name: templateToUse,
                status: 'failed',
                error_message: `Excepción JS: ${e?.message || String(e)}`,
                order_id: orderNum,
            });
        } catch (logErr) {
            console.error('No pude loguear el fallo de WA:', logErr);
        }
    }
}

// Bloques del formulario (a nivel de módulo: si se definen dentro del render, React los
// recrea en cada tecla y los campos pierden el foco).
function Section({ n, title, right, children }: { n: number; title: string; right?: React.ReactNode; children: React.ReactNode }) {
    return (
    <section className="rounded-[var(--radius-surface)] border border-line bg-card p-4 sm:p-5">
        <div className="mb-4 flex items-center justify-between gap-3">
            <h3 className="flex items-center gap-2.5 text-[length:var(--text-base)] font-semibold text-ink">
                <span className="grid h-6 w-6 place-items-center rounded-full bg-[var(--foreground)] text-[length:var(--text-xs)] font-semibold text-[var(--card)]">{n}</span>
                {title}
            </h3>
            {right}
        </div>
        {children}
    </section>
    );
}
function SumRow({ label, value, tone }: { label: React.ReactNode; value: React.ReactNode; tone?: 'ok' | 'muted' }){
    return (
    <div className={`flex items-baseline justify-between gap-3 py-1 text-[length:var(--text-sm)] ${tone === 'ok' ? 'text-[var(--success)]' : tone === 'muted' ? 'text-faint' : 'text-soft'}`}>
        <span className="min-w-0">{label}</span><span className="shrink-0 tabular-nums">{value}</span>
    </div>
    );
}

type NewOrderProps = {
    onCancel: () => void;
    onSuccess: () => void;
    showSnackbar: (msg: string, type: 'success' | 'error') => void;
    initialOrder?: any;
    /** Canje a creadores (usuario rol 'canje'): total $0, sin pago y sin WhatsApp. */
    canje?: boolean;
};

type OrderItem = {
    name: string;
    price: number;
    quantity: number;
    tn_variant_id?: number;
    list_price?: number; // precio antes de la promo de catálogo (para calcular promo_discount)
};

interface TnProduct {
    id: number;
    name: string;
    image_url: string | null;
    category?: string | null;
    is_stock?: boolean | null;
    lead_days?: number | null;   // días de fabricación (null = default de cost_settings)
}

interface Promo {
    id: number;
    name: string;
    discount_percent: number;
    scope: string;          // 'all' | 'category'
    category: string | null;
    start_date: string | null;
    end_date: string | null;
    active: boolean;
}

interface TnVariant {
    id: number;
    product_id: number;
    price: number | null;
    promotional_price: number | null;
    values: string[];
}

export function NewOrder({ onCancel, onSuccess, showSnackbar, initialOrder, canje }: NewOrderProps) {
    // ── Modo CANJE (13/10/2026) ── pedido para un creador de contenido: entra al taller como
    // cualquier orden, pero total $0 (no suma a facturación), pago "canje" y contact_phone NULL
    // (todo el sistema exige contact_phone para mandar WhatsApp → no sale ninguno). El teléfono
    // queda en canje_phone, solo para el envío. Marca is_canje para estadísticas.
    const isCanje = !!canje || !!initialOrder?.is_canje;
    // lat/lon de la dirección si vino de un autocompletado. Con coordenadas Cabify no
    // geocodifica el texto, que es justo donde falla (ver migr 096).
    const [deliveryCoords, setDeliveryCoords] = useState<{ lat: number; lon: number } | null>(
        (initialOrder as any)?.delivery_lat != null
            ? { lat: Number((initialOrder as any).delivery_lat), lon: Number((initialOrder as any).delivery_lon) }
            : null,
    );
    const [formData, setFormData] = useState({
        contact_name: initialOrder?.contact_name || '',
        contact_phone: initialOrder?.contact_phone || initialOrder?.canje_phone || '',
        // Venta nueva: arranca en Showroom (27/10/2026, antes Instagram). Canjes siguen con 'ig'.
        source: initialOrder?.source || (canje ? 'ig' : 'showroom'),
        discount: initialOrder?.discount || 0,
        // descuento extra en % (default) o monto fijo $ (28/10/2026, columna discount_type)
        discount_type: ((initialOrder as any)?.discount_type === 'fixed' ? 'fixed' : 'pct') as 'pct' | 'fixed',
        note: initialOrder?.note || '',
        delivery_type: initialOrder?.delivery_type || 'retiro',
        delivery_address: initialOrder?.delivery_address || '',
        delivery_unit: (initialOrder as any)?.delivery_unit || '',
        is_circulo_rojo: (initialOrder as any)?.is_circulo_rojo || false,
        shipping_cost: (initialOrder as any)?.shipping_cost_customer || 0,
    });

    // Búsqueda de cliente en la base
    type CustomerHit = {
        id: string;
        name: string;
        email: string | null;
        phone: string | null;
        phone_display: string | null;
        circulo_rojo: boolean;
        first_order_at: string | null;
        last_order_at: string | null;
        order_count: number;
        total_spent: number;
        coupon_code: string | null;
        notes: string | null;
    };
    const [saving, setSaving] = useState(false); // guard anti doble-submit (evita crear orden/presupuesto duplicado)
    const [showCustomerSearch, setShowCustomerSearch] = useState(false);
    const [customerSearchQuery, setCustomerSearchQuery] = useState('');
    const [customerSearchResults, setCustomerSearchResults] = useState<CustomerHit[]>([]);
    const [customerSearching, setCustomerSearching] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState<CustomerHit | null>(null);

    // Debounce de búsqueda
    useEffect(() => {
        if (!showCustomerSearch) return;
        const q = customerSearchQuery.trim();
        if (q.length < 2) { setCustomerSearchResults([]); return; }
        const t = setTimeout(async () => {
            setCustomerSearching(true);
            try {
                const digits = q.replace(/\D/g, '');
                let qb = supabase
                    .from('customers')
                    .select('id, name, email, phone, phone_display, circulo_rojo, first_order_at, last_order_at, order_count, total_spent, coupon_code, notes')
                    .order('circulo_rojo', { ascending: false })
                    .order('last_order_at', { ascending: false, nullsFirst: false })
                    .limit(20);
                if (digits.length >= 6) {
                    qb = qb.or(`name.ilike.%${q}%,email.ilike.%${q}%,phone.ilike.%${digits}%`);
                } else {
                    qb = qb.or(`name.ilike.%${q}%,email.ilike.%${q}%`);
                }
                const { data, error } = await qb;
                if (error) throw error;
                setCustomerSearchResults((data || []) as CustomerHit[]);
            } catch (e) {
                console.error('Error buscando clientes:', e);
                setCustomerSearchResults([]);
            } finally {
                setCustomerSearching(false);
            }
        }, 250);
        return () => clearTimeout(t);
    }, [customerSearchQuery, showCustomerSearch]);

    function pickCustomer(c: CustomerHit) {
        setSelectedCustomer(c);
        setFormData(prev => ({
            ...prev,
            contact_name: c.name,
            contact_phone: c.phone || c.phone_display || prev.contact_phone,
            // Si el cliente es CR y aún no tiene flag/descuento, lo marcamos y aplicamos 10%.
            is_circulo_rojo: c.circulo_rojo ? true : prev.is_circulo_rojo,
            discount: c.circulo_rojo && (!prev.discount || prev.discount < 10) ? 10 : prev.discount,
        }));
        setShowCustomerSearch(false);
        setCustomerSearchQuery('');
        setCustomerSearchResults([]);
    }

    function fmtDate(iso: string | null): string {
        if (!iso) return '—';
        try { return new Date(iso).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' }); }
        catch { return '—'; }
    }

    const [paymentStatus, setPaymentStatus] = useState<'paid' | 'pending' | 'pending_cash'>(() => {
        if (initialOrder?.payment_status === 'paid') return 'paid';
        const isPending = initialOrder?.payment_status === 'pending' || initialOrder?.payment_status === 'voided';
        if (isPending && initialOrder?.payment_details?.method === 'custom') return 'pending_cash';
        return 'pending';
    });

    const [paymentMethod, setPaymentMethod] = useState<'efectivo' | 'transferencia' | 'tarjeta'>(() => {
        // Órdenes de canal (TN/ML): manda el medio REAL del gateway (03/11/2026). Antes, si la orden
        // no tenía payment_method cargado, el editor defaulteaba 'tarjeta' y al guardar lo escribía
        // en órdenes pagadas por transferencia (#4796 y ~20 más).
        const gw = String((initialOrder as any)?.gateway_name || '').toLowerCase();
        const pdMethod = String(initialOrder?.payment_details?.method || '');
        const channel = ['tiendanube', 'mercadolibre', 'woocommerce'].includes(String(initialOrder?.source || ''));
        if (channel) {
            if (pdMethod === 'wire_transfer' || /transferencia|dep[oó]sito/.test(gw)) return 'transferencia';
            if (/efectivo|cash/.test(gw)) return 'efectivo';
            if (pdMethod === 'credit_card' || pdMethod === 'debit_card' || /tarjeta|cuotas|pago\s*nube|mercado\s*pago/.test(gw)) return 'tarjeta';
        }
        const m = initialOrder?.payment_details?.payment_method;
        if (m === 'efectivo' || m === 'transferencia' || m === 'tarjeta') return m;
        if (pdMethod === 'custom') return 'efectivo';
        return 'tarjeta';
    });

    // Transferencia en venta manual: ¿a qué cuenta va? OBLIGATORIO (pedido 10/10/2026). Lo usa el
    // control de transferencias: 'financiera' tiene que aparecer en el estado de cuenta; 'krilic'
    // queda fuera de ese control. Se guarda en payment_details.transfer_account.
    const [transferAccount, setTransferAccount] = useState<'financiera' | 'krilic' | null>(
        () => initialOrder?.payment_details?.transfer_account ?? null);
    const [transferAccountError, setTransferAccountError] = useState(false);
    // Comprobante de la transferencia al CREAR una venta manual (pedido 12/10/2026): obligatorio si se
    // marca "Pagado" (sin comprobante no se puede controlar contra el estado de cuenta: Julián
    // K-25459). Se sube después de crear la orden a whatsapp-media → orders.receipt_urls.
    const [receiptFiles, setReceiptFiles] = useState<File[]>([]);
    const [receiptError, setReceiptError] = useState(false);

    // Pendiente en efectivo = medio efectivo + no pagado (la UI ya no lo pregunta aparte).
    useEffect(() => {
        setPaymentStatus(ps => (ps === 'paid' ? ps : paymentMethod === 'efectivo' ? 'pending_cash' : 'pending'));
    }, [paymentMethod]);

    const isBudgetEdit = initialOrder?.status === 'presupuesto';
    // Cupón de Tiendanube en ventas manuales (28/10/2026): se valida contra TN (edge fn tn-coupon),
    // se aplica después del descuento por medio de pago y del descuento extra, y al guardar la orden
    // se descuenta UN uso en TN. Se guarda en orders.coupon_code / coupon_amount y la definición en
    // payment_details.coupon (para recalcular al editar). No aplica a órdenes de TN/Woo ni a canjes.
    type TnCoupon = { code: string; type: 'percentage' | 'absolute' | 'shipping'; value: number; products: number[] };
    const initialCoupon: TnCoupon | null = (initialOrder as any)?.payment_details?.coupon || null;
    const [coupon, setCoupon] = useState<TnCoupon | null>(initialCoupon);
    const [couponInput, setCouponInput] = useState('');
    const [couponMsg, setCouponMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
    const [couponChecking, setCouponChecking] = useState(false);
    // Solo ventas propias: en Tiendanube/ML la transferencia va siempre a la financiera (datos del WhatsApp)
    const needsTransferAccount = paymentMethod === 'transferencia'
        && !['tiendanube', 'mercadolibre', 'woocommerce'].includes(formData.source || '');
    // En órdenes de canal la cuenta también se puede elegir (02/11/2026, #5137): a veces el cliente
    // transfiere a la cuenta de Krilic. Opcional: sin elegir = financiera (como siempre).
    const showTransferAccount = paymentMethod === 'transferencia' && !isCanje;
    // Tiendanube/WooCommerce mandan el descuento como MONTO absoluto (no %), y ese monto ya
    // ES el del medio de pago (viene reflejado en total_price). En esas órdenes: (a) tratamos
    // el descuento como monto fijo, no como %, y (b) NO aplicamos el % de medio de pago manual
    // (sería doble descuento). Así el total del editor cuadra con total_price (igual que card/PDF).
    const isAbsoluteDiscount = initialOrder?.source === 'tiendanube' || initialOrder?.source === 'woocommerce';
    // Descuento por medio de pago: efectivo y transferencia arrancan en 15% (política
    // 2026-08-08: ambos iguales) pero el % es EDITABLE por orden — si se negocia otro
    // número se corrige acá y queda guardado en payment_details.pm_discount_pct.
    const DEFAULT_PM_PCT: Record<'efectivo' | 'transferencia' | 'tarjeta', number> = { efectivo: 15, transferencia: 15, tarjeta: 0 };
    const [pmPctOverride, setPmPctOverride] = useState<number | null>(() => {
        const saved = initialOrder?.payment_details?.pm_discount_pct;
        if (saved === 0 || saved) return Number(saved);
        // Órdenes viejas SIN el % guardado (antes del 08/08 la transferencia era 10%): se DEDUCE del
        // total que ya tienen. Sin esto el popup aplicaba el 15% de hoy, mostraba otro total y, al
        // guardar cualquier cambio, reescribía total_price (K-23733: $114.300 → $107.950, 12/10/2026).
        const pm = String(initialOrder?.payment_details?.payment_method || '').toLowerCase();
        if (initialOrder && initialOrder.status !== 'presupuesto' && (pm === 'efectivo' || pm === 'transferencia')) {
            const sub = (initialOrder.products || []).reduce((a: number, p: any) => a + (Number(p.price) || 0) * (Number(p.quantity) || 1), 0);
            const disc = Number(initialOrder.discount) || 0;
            const ship = Number((initialOrder as any).shipping_cost_customer) || 0;
            const saved$ = Number(initialOrder.total_price);
            for (const pct of [15, 10, 0]) {
                const t = Math.round(sub * (1 - pct / 100) * (1 - disc / 100) * 100) / 100 + ship;
                if (Math.abs(t - saved$) <= 1) return pct;
            }
        }
        return null;
    });
    // Tilde para NO aplicar el descuento del medio de pago en esta orden (ej: transferencia
    // sin el 15%, porque ya se negoció otro precio). Queda guardado en el payment_details,
    // así al reabrir la orden el total sigue cuadrando.
    const [sinPmDiscount, setSinPmDiscount] = useState<boolean>(
        () => initialOrder?.payment_details?.pm_discount_off === true
            || initialOrder?.payment_details?.pm_discount_pct === 0,
    );
    // En presupuestos el descuento por medio de pago no se aplica (el total guardado es el
    // precio "lista" y el PDF muestra las alternativas como filas aparte).
    const paymentMethodDiscountPct = (isBudgetEdit || isAbsoluteDiscount || sinPmDiscount)
        ? 0
        : (pmPctOverride ?? DEFAULT_PM_PCT[paymentMethod]);

    // Seña: pago parcial al confirmar; el resto se cobra al retirar/entregar.
    const [deposit, setDeposit] = useState<number>(Number((initialOrder as any)?.deposit_amount) || 0);

    // "Envío gratis": envío a domicilio sin costo para el cliente, explícito (distinto de
    // "no cargaron el costo"). Se persiste en shipping_option='Envio Gratis' y sale en el PDF.
    const [freeShipping, setFreeShipping] = useState<boolean>(() =>
        /gratis/i.test((initialOrder as any)?.shipping_option || ''));
    // Zona de envío detectada al elegir la dirección (src/shippingZones.ts) → precarga el costo.
    // Si el costo ya lo eligió una persona (o la orden ya lo tenía) NO se pisa.
    const [shipZone, setShipZone] = useState<{ zone: ShippingZone; partido: string } | null>(null);
    const [shippingTouched, setShippingTouched] = useState<boolean>(() =>
        !!initialOrder && (Number((initialOrder as any)?.shipping_cost_customer) > 0 || /gratis/i.test((initialOrder as any)?.shipping_option || '')));
    const onAddressPlace = (pl: { components: { longText?: string; types?: string[] }[] }) => {
        const zone = zoneFromComponents(pl.components);
        if (!zone) { setShipZone(null); return; }
        const comp = (t: string) => pl.components.find(c => (c.types || []).includes(t))?.longText || '';
        // En CABA Google devuelve "Comuna 13" como partido: se muestra el barrio
        const partido = zone === 'caba'
            ? (comp('sublocality_level_1') || comp('neighborhood') || 'CABA')
            : (comp('administrative_area_level_2') || comp('locality'));
        setShipZone({ zone, partido });
        if (shippingTouched) return;
        setFreeShipping(false);
        setFormData(fd => ({ ...fd, shipping_cost: zone === 'fuera' ? 0 : ZONE_PRICE[zone] }));
    };

    const [items, setItems] = useState<OrderItem[]>(
        initialOrder?.products
            // Se conserva TODO lo que trae el ítem (sku, tn_product_id, is_cut, is_picked, meli_item_id…):
            // antes se cargaban solo 5 campos y al guardar se perdía el resto (12/10/2026).
            ? initialOrder.products.map((p: any) => ({ ...p, name: p.name, price: p.price || 0, quantity: p.quantity || 1 }))
            : []
    );

    // Tiendanube products for autocomplete
    const [tnProducts, setTnProducts] = useState<TnProduct[]>([]);
    // ── Fecha de LISTO (empaquetado) ── misma regla que el trigger de la base (migr 098):
    // compra + el producto MÁS LENTO, días corridos; producto en stock = 0 días.
    const [defaultLeadDays, setDefaultLeadDays] = useState<number>(7);
    const [readyBy, setReadyBy] = useState<string>((initialOrder as any)?.ready_by || '');
    const [readyPromised, setReadyPromised] = useState<boolean>((initialOrder as any)?.ready_by_kind === 'promised');
    // true cuando la fecha la eligió una persona: deja de seguir a la sugerida
    const [readyTouched, setReadyTouched] = useState<boolean>(!!(initialOrder as any)?.ready_by && initialOrder?.status !== 'presupuesto');
    const [isUrgent, setIsUrgent] = useState<boolean>(!!(initialOrder as any)?.is_urgent);
    const [tnVariants, setTnVariants] = useState<TnVariant[]>([]);
    const [activePromos, setActivePromos] = useState<Promo[]>([]);

    // Buscador del catálogo (inline, desplegable)
    const [showProductModal, setShowProductModal] = useState(false);
    const [modalSearch, setModalSearch] = useState('');
    const [expandedProductId, setExpandedProductId] = useState<number | null>(null);

    useEffect(() => {
        // Load TN products and variants for the product selector
        async function loadTnProducts() {
            try {
                const pRes = await supabase.from('tn_products').select('id, name, image_url, category, is_stock, lead_days').eq('active', true).order('name');
                if (pRes.data) setTnProducts(pRes.data);
                const cs = await supabase.from('cost_settings').select('default_lead_days').eq('id', 1).maybeSingle();
                if (cs.data?.default_lead_days != null) setDefaultLeadDays(Number(cs.data.default_lead_days));

                // Paginar tn_variants: PostgREST tiene hard cap 1000 por request, y la tabla tiene más.
                const allVariants: any[] = [];
                const pageSize = 1000;
                for (let offset = 0; offset < 20000; offset += pageSize) {
                    const { data, error } = await supabase
                        .from('tn_variants')
                        .select('id, product_id, price, promotional_price, values')
                        .order('id', { ascending: true })
                        .range(offset, offset + pageSize - 1);
                    if (error || !data || data.length === 0) break;
                    allVariants.push(...data);
                    if (data.length < pageSize) break;
                }
                setTnVariants(allVariants.map((v: any) => ({
                    ...v,
                    values: typeof v.values === 'string' ? JSON.parse(v.values) : (v.values || [])
                })));
            } catch (e) {
                console.error('Error loading TN products:', e);
            }
        }
        loadTnProducts();

        // Promos de catálogo vigentes (definidas a mano en el ABM). Se aplican al precio
        // de línea al agregar un producto, replicando lo que hace TN en la web.
        async function loadPromos() {
            try {
                const { data } = await supabase.from('promotions').select('*').eq('active', true);
                const now = Date.now();
                const vigentes = (data || []).filter((p: Promo) => {
                    const okStart = !p.start_date || new Date(p.start_date).getTime() <= now;
                    const okEnd = !p.end_date || new Date(p.end_date).getTime() >= now;
                    return okStart && okEnd;
                });
                setActivePromos(vigentes);
            } catch (e) {
                console.error('Error loading promos:', e);
            }
        }
        loadPromos();
    }, []);

    // Devuelve el % de promo que aplica a un producto (0 si ninguna). Prioriza la de mayor descuento.
    // scope 'product': product_pattern = substrings separados por | contra el nombre (ej "infinite").
    function promoPctForProduct(product?: TnProduct): number {
        if (!activePromos.length) return 0;
        const cat = product?.category || null;
        const pname = (product?.name || '').toLowerCase();
        const matches = activePromos.filter(p =>
            p.scope === 'all'
            || (p.scope === 'category' && cat && p.category === cat)
            || (p.scope === 'product' && (p as any).product_pattern && pname
                && String((p as any).product_pattern).toLowerCase().split('|').some(pat => pat.trim() && pname.includes(pat.trim())))
        );
        if (!matches.length) return 0;
        return Math.max(...matches.map(p => Number(p.discount_percent) || 0));
    }

    // Días de fabricación de un ítem: por variante → producto, o por nombre (prefijo).
    function itemLeadDays(it: OrderItem): { days: number; product?: TnProduct } {
        let prod: TnProduct | undefined;
        if (it.tn_variant_id) {
            const v = tnVariants.find(x => x.id === it.tn_variant_id);
            if (v) prod = tnProducts.find(p => p.id === v.product_id);
        }
        if (!prod) {
            const nm = (it.name || '').toLowerCase();
            prod = tnProducts.filter(p => p.name && nm.startsWith(p.name.toLowerCase()))
                .sort((a, b) => b.name.length - a.name.length)[0];
        }
        if (!prod) return { days: defaultLeadDays };
        if (prod.is_stock) return { days: 0, product: prod };
        return { days: prod.lead_days ?? defaultLeadDays, product: prod };
    }
    const readySuggestion = (() => {
        // base: la fecha de compra de la orden que se edita; si es nueva (o un presupuesto que
        // se convierte), hoy.
        const base = initialOrder && initialOrder.status !== 'presupuesto' && initialOrder.created_at
            ? new Date(initialOrder.created_at) : new Date();
        let max = 0; let slow: string | null = null;
        for (const it of items) {
            if (!it.name) continue;
            const r = itemLeadDays(it);
            if (slow == null || r.days > max) { max = r.days; slow = it.name; }
        }
        const d = new Date(base.getTime() + max * 86400000);
        // fecha local AR (yyyy-mm-dd) — en-CA da ese formato
        const iso = d.toLocaleDateString('en-CA', { timeZone: 'America/Argentina/Buenos_Aires' });
        return { iso, days: max, slow: items.filter(i => i.name).length ? slow : null };
    })();
    // Mientras nadie la toque, la fecha sigue a la sugerida (al agregar/sacar productos).
    useEffect(() => {
        if (!readyTouched && readySuggestion.iso !== readyBy) setReadyBy(readySuggestion.iso);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [readySuggestion.iso, readyTouched]);

    // Simplified ID logic - we rely on DB serial IDs now
    const nextIdDisplay = initialOrder
        ? (() => {
            if (initialOrder.source === 'woocommerce' && initialOrder.tiendanube_id) {
                return `#BT-${initialOrder.tiendanube_id.replace(/^(WC-|BT-)/i, '')}`;
            }
            return initialOrder.tiendanube_id ? `#${initialOrder.tiendanube_id}` : `K-${initialOrder.id}`;
        })()
        : 'Autogenerado';

    const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    // Monto descontado por promo de catálogo (informativo + se guarda en promo_discount).
    const promoDiscountTotal = items.reduce(
        (sum, item) => sum + Math.max(0, (Number(item.list_price) || 0) - item.price) * item.quantity,
        0
    );
    // Descuentos en cascada: primero el de medio de pago sobre el subtotal,
    // y el descuento manual se aplica sobre el resultado de ese paso.
    // Todo redondeado a 2 decimales (los % generaban totales tipo $99.001,5001).
    const round2 = (n: number) => Math.round(n * 100) / 100;
    const pmDiscountAmount = round2((subtotal * paymentMethodDiscountPct) / 100);
    const afterPm = subtotal - pmDiscountAmount;
    // TN/Woo: el descuento es un MONTO fijo. Manual: es un % sobre el subtotal ya con medio de pago.
    const discountAmount = round2(isAbsoluteDiscount
        ? (Number(formData.discount) || 0)
        : formData.discount_type === 'fixed'
            ? Math.min(Number(formData.discount) || 0, afterPm)
            : (afterPm * formData.discount) / 100);
    // Cupón TN: sobre lo que queda después del medio de pago y el descuento extra. Si el cupón está
    // limitado a productos, solo sobre la parte proporcional de esos productos.
    const beforeCoupon = Math.max(0, afterPm - discountAmount);
    const couponActive = !!coupon && !isAbsoluteDiscount && !isCanje;
    const couponBase = (() => {
        if (!couponActive || !coupon) return 0;
        if (!coupon.products?.length || !subtotal) return beforeCoupon;
        const elig = items.reduce((s2, it: any) => s2 + (coupon.products.includes(Number(it.tn_product_id)) ? it.price * it.quantity : 0), 0);
        return beforeCoupon * (elig / subtotal);
    })();
    const couponAmount = !couponActive || !coupon ? 0 : round2(
        coupon.type === 'percentage' ? couponBase * coupon.value / 100
        : coupon.type === 'absolute' ? Math.min(coupon.value, couponBase) : 0);
    const total = round2(Math.max(0, beforeCoupon - couponAmount));
    // El envío se suma al total (no se descuenta). La facturación de productos es aparte.
    // Cupón de envío gratis: el envío al cliente queda en 0.
    const shippingCost = couponActive && coupon?.type === 'shipping' ? 0 : (Number(formData.shipping_cost) || 0);
    const grandTotal = total + shippingCost;

    const handleItemChange = (index: number, field: keyof OrderItem, value: any) => {
        const newItems = [...items];
        // @ts-ignore
        newItems[index] = { ...newItems[index], [field]: value };
        setItems(newItems);
    };

    const addItem = () => {
        setItems([...items, { name: '', price: 0, quantity: 1 }]);
    };
    // ── MARCO A MEDIDA (28/10/2026) ── Marco Doble Panel Flotante con tamaño personalizado.
    // Se carga la medida de la LÁMINA; el marco final suma 2,5 cm de margen por lado (+5 cm por
    // medida, igual que las medidas del catálogo: 50×50 → contenido 45×45). Precio proporcional
    // al área del marco final con una recta (fijo + $/cm²) ajustada EN VIVO sobre las variantes del
    // catálogo (mismo precio base que el buscador: promocional si hay). Error del ajuste: ±4%.
    const FRAME_PRODUCT_ID = 220427904;
    const FRAME_MARGIN_CM = 2.5;
    const frameModel = useMemo(() => {
        const pts = tnVariants.filter(v => v.product_id === FRAME_PRODUCT_ID).map(v => {
            const m = String(v.values?.[0] || '').match(/(\d+(?:[.,]\d+)?)\s*x\s*(\d+(?:[.,]\d+)?)/i);
            const price = Number(v.promotional_price || v.price) || 0;
            return m && price ? { area: parseFloat(m[1].replace(',', '.')) * parseFloat(m[2].replace(',', '.')), price } : null;
        }).filter(Boolean) as { area: number; price: number }[];
        if (pts.length < 2) return null;
        const n = pts.length, mx = pts.reduce((a, p) => a + p.area, 0) / n, my = pts.reduce((a, p) => a + p.price, 0) / n;
        const b = pts.reduce((a, p) => a + (p.area - mx) * (p.price - my), 0) / pts.reduce((a, p) => a + (p.area - mx) ** 2, 0);
        return { fixed: my - b * mx, perCm2: b };
    }, [tnVariants]);
    const [frameForm, setFrameForm] = useState<{ open: boolean; w: string; h: string; q: string }>({ open: false, w: '', h: '', q: '1' });
    const frameQuote = (() => {
        const w = parseFloat(frameForm.w.replace(',', '.')), h = parseFloat(frameForm.h.replace(',', '.'));
        if (!frameModel || !(w > 0) || !(h > 0)) return null;
        const fw = w + 2 * FRAME_MARGIN_CM, fh = h + 2 * FRAME_MARGIN_CM;
        const price = Math.round((frameModel.fixed + frameModel.perCm2 * fw * fh) / 100) * 100;
        const fmt = (x: number) => String(Math.round(x * 10) / 10).replace('.', ',');
        return { fw, fh, price, name: `Marco Doble Panel Flotante a medida – final ${fmt(fw)}×${fmt(fh)} cm (lámina ${fmt(w)}×${fmt(h)} cm)` };
    })();
    const addFrame = () => {
        if (!frameQuote) return;
        const q = Math.max(1, parseInt(frameForm.q, 10) || 1);
        const newItem: any = { name: frameQuote.name, price: frameQuote.price, quantity: q, tn_product_id: FRAME_PRODUCT_ID };
        setItems(items.length === 1 && !items[0].name && items[0].price === 0 ? [newItem] : [...items, newItem]);
        setFrameForm(f => ({ ...f, w: '', h: '', q: '1' }));
        showSnackbar(`Marco ${frameQuote.fw}×${frameQuote.fh} agregado`, 'success');
    };

    // Producto FUERA del catálogo desde el buscador (14/10/2026): antes, si no estaba en el catálogo,
    // el buscador decía "No hay productos" y no había forma obvia de cargarlo. Se agrega con el
    // nombre escrito y el foco va al precio.
    const [focusPriceIdx, setFocusPriceIdx] = useState<number | null>(null);
    const addCustomItem = (name: string) => {
        const n = name.trim();
        setFocusPriceIdx(items.length);
        setItems([...items, { name: n, price: 0, quantity: 1 }]);
        setModalSearch(''); setShowProductModal(false);
    };

    const removeItem = (index: number) => {
        setItems(items.filter((_, i) => i !== index));
    };

    function getVariantsForProduct(productId: number): TnVariant[] {
        return tnVariants.filter(v => v.product_id === productId);
    }

    function addProductToOrder(product: TnProduct, variant?: TnVariant) {
        let name = product.name;
        let base = 0; // precio de lista (con precio especial de la variante si tiene)
        let tn_variant_id: number | undefined;

        if (variant) {
            const variantLabel = variant.values.length > 0 ? ` – ${variant.values.join(' / ')}` : '';
            name += variantLabel;
            base = variant.promotional_price || variant.price || 0;
            tn_variant_id = variant.id;
        } else {
            // Check if it's a single variant product
            const pvs = getVariantsForProduct(product.id);
            if (pvs.length === 1) {
                const v = pvs[0];
                const variantLabel = v.values.length > 0 ? ` – ${v.values.join(' / ')}` : '';
                name += variantLabel;
                base = v.promotional_price || v.price || 0;
                tn_variant_id = v.id;
            }
        }

        // Aplicar promo de catálogo vigente (si corresponde al producto/categoría).
        // NUNCA sobre un precio que ya es promocional en Tiendanube (30/10/2026, K-26358: Reloj de Pared
        // Infinite a $71.200 de TN − 15% de "Lámparas Infinite" = $60.520). TN no combina promos.
        const chosen = variant || (getVariantsForProduct(product.id).length === 1 ? getVariantsForProduct(product.id)[0] : undefined);
        const tnPromo = !!chosen?.promotional_price && Number(chosen.promotional_price) < Number(chosen.price);
        const pct = tnPromo ? 0 : promoPctForProduct(product);
        const price = pct > 0 ? Math.round(base * (1 - pct / 100) * 100) / 100 : base;
        const newItem: OrderItem = { name, price, quantity: 1, tn_variant_id };
        if (pct > 0 && price < base) newItem.list_price = base;

        // If items has only 1 row and it's empty, replace it. Otherwise, append.
        if (items.length === 1 && !items[0].name && items[0].price === 0) {
            setItems([newItem]);
        } else {
            setItems([...items, newItem]);
        }

        showSnackbar(
            pct > 0 ? `${name} agregado (promo -${pct}% aplicada)` : `${name} agregado al pedido`,
            'success'
        );
    }

    // Modal specific search. Ignora tildes, guiones y espacios ("ta te ti" / "tateti" encuentran
    // "Ta-Te-Ti"), y también acepta las palabras en cualquier orden ("lampara luna" → "Luna Lámpara").
    const searchKey = (s: string) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const modalFilteredProducts = modalSearch.trim()
        ? tnProducts.filter(p => {
            const name = searchKey(p.name || '');
            const q = searchKey(modalSearch);
            const compact = (s: string) => s.replace(/[^a-z0-9]/g, '');
            if (compact(name).includes(compact(q))) return true;
            const words = q.split(/[^a-z0-9]+/).filter(Boolean);
            return words.length > 1 && words.every(w => compact(name).includes(w));
        }).slice(0, 15)
        : tnProducts.slice(0, 15);

    const handleSubmit = async (status: 'presupuesto' | 'nueva') => {
        if (saving) return; // ya hay un guardado en curso: ignoramos el segundo click (anti-duplicado)
        // Showroom permite vender sin cargar cliente (queda como "Showroom").
        const effectiveName = (formData.contact_name || '').trim()
            || (formData.source === 'showroom' ? 'Showroom' : '');
        if (!effectiveName) {
            showSnackbar('Falta nombre del cliente', 'error');
            return;
        }
        if (items.length === 0 || !items[0].name) {
            showSnackbar('Agrega al menos un producto con nombre', 'error');
            return;
        }
        // La orden entra a la cola del taller por esta fecha: sin ella no se puede priorizar.
        if (status === 'nueva' && !readyBy) {
            showSnackbar('Falta la fecha de listo (empaquetado)', 'error');
            return;
        }
        if (status === 'nueva' && !initialOrder && needsTransferAccount && paymentStatus === 'paid' && receiptFiles.length === 0) {
            setReceiptError(true);
            showSnackbar('Adjuntá el comprobante de la transferencia (es obligatorio si está pagada)', 'error');
            return;
        }
        if (status === 'nueva' && !isBudgetEdit && needsTransferAccount && !transferAccount) {
            setTransferAccountError(true);
            showSnackbar('Elegí a qué cuenta va la transferencia: Financiera o Krilic', 'error');
            return;
        }

        setSaving(true);
        try {
            // En presupuestos, el descuento por medio de pago NO se aplica al total guardado.
            // El precio guardado representa el precio "lista" (tarjeta), y el PDF muestra
            // las alternativas (efectivo / transferencia) calculadas sobre ese total.
            const presupuestoDiscountAmount = isAbsoluteDiscount
                ? (Number(formData.discount) || 0)
                : formData.discount_type === 'fixed'
                    ? Math.min(Number(formData.discount) || 0, subtotal)
                    : (subtotal * formData.discount) / 100;
            const totalForSave = status === 'presupuesto'
                ? Math.max(0, subtotal - presupuestoDiscountAmount)
                : total;

            // orden existente (no presupuesto) que se edita en el lugar
            const keepProgress = !!initialOrder && initialOrder.status !== 'presupuesto' && status === 'nueva';
            const payload = {
                contact_name: effectiveName,
                contact_phone: isCanje ? null : formData.contact_phone,
                ...(isCanje ? { is_canje: true, canje_phone: formData.contact_phone || null } : {}),
                source: formData.source,
                discount: formData.discount,
                ...(isAbsoluteDiscount ? {} : { discount_type: formData.discount_type === 'fixed' ? 'fixed' : 'pct' }),
                is_circulo_rojo: formData.is_circulo_rojo,
                // Editar una orden NO toca el avance del taller: se conservan el estado y las marcas de
                // cortado/retirado de cada producto. Antes "Guardar cambios" mandaba status 'nueva' e
                // is_cut=false siempre: cambiar el medio de pago (o cualquier dato) devolvía la orden a
                // "nueva" (12/10/2026). Ítems nuevos entran sin cortar.
                products: items.map(i => ({ ...i, is_cut: keepProgress ? !!(i as any).is_cut : false })),
                status: keepProgress ? initialOrder!.status : status,
                total_price: isCanje ? 0 : round2(totalForSave + shippingCost),
                promo_discount: status === 'nueva' ? promoDiscountTotal : undefined,
                shipping_cost_customer: shippingCost,
                // cupón TN: solo en órdenes (budgets no tiene estas columnas)
                ...(status === 'nueva' && !isAbsoluteDiscount && !isCanje ? { coupon_code: couponActive ? coupon!.code : null, coupon_amount: couponActive ? couponAmount : null } : {}),
                // 'Envio Gratis' explícito; al editar una orden de TN que ya venía gratis se
                // preserva su texto original ("Envio Gratis (Recomendado)"). Destildarlo lo limpia.
                shipping_option: formData.delivery_type === 'envio'
                    ? (freeShipping
                        ? (/gratis/i.test((initialOrder as any)?.shipping_option || '') ? (initialOrder as any).shipping_option : 'Envio Gratis')
                        : (/gratis/i.test((initialOrder as any)?.shipping_option || '') ? null : ((initialOrder as any)?.shipping_option ?? null)))
                    : ((initialOrder as any)?.shipping_option ?? null),
                delivery_type: formData.delivery_type,
                delivery_address: formData.delivery_address,
                // Piso/depto aparte: la dirección queda limpia para Google (ver src/geocode.ts)
                delivery_unit: formData.delivery_type === 'envio' ? (formData.delivery_unit.trim() || null) : null,
                delivery_lat: formData.delivery_type === 'envio' ? (deliveryCoords?.lat ?? null) : null,
                delivery_lon: formData.delivery_type === 'envio' ? (deliveryCoords?.lon ?? null) : null,
                note: formData.note,
                ready_by: readyBy || null,
                ready_by_kind: readyPromised ? 'promised' : 'auto',
                is_urgent: isUrgent,
                payment_status: status === 'nueva' ? (paymentStatus === 'paid' ? 'paid' : 'pending') : undefined,
                deposit_amount: status === 'nueva' ? (paymentStatus === 'paid' ? 0 : deposit) : undefined,
                payment_details: status === 'nueva'
                    ? { ...initialOrder?.payment_details, method: paymentStatus === 'pending_cash' ? 'custom' : 'other', payment_method: paymentMethod, pm_discount_pct: paymentMethodDiscountPct, pm_discount_off: sinPmDiscount,
                        coupon: couponActive ? coupon : null,
                        transfer_account: paymentMethod === 'transferencia' && showTransferAccount ? transferAccount : (initialOrder?.payment_details?.transfer_account ?? null) }
                    : undefined,
                // canje: sin cobro (no entra a pendientes, Transferencias ni BI de pagos), sin descuentos
                ...(isCanje && status === 'nueva' ? {
                    payment_status: 'paid', deposit_amount: 0, promo_discount: 0, shipping_cost_customer: 0,
                    payment_details: { method: 'other', payment_method: 'canje' },
                } : {}),
            };

            let error;

            if (initialOrder) {
                // Determine table based on initial status or current status
                // If status changed from 'presupuesto' to 'nueva', we need to move it
                if (initialOrder.status === 'presupuesto' && status === 'nueva') {
                    // Move from budgets to orders
                    // 1. Create in orders
                    const { data: newOrder, error: createError } = await supabase
                        .from('orders')
                        .insert({
                            ...payload,
                            tiendanube_id: null, // Manual order has no tiendanube_id
                            status: 'nueva',
                            created_at: new Date().toISOString()
                        })
                        .select()
                        .single();

                    if (createError) throw createError;

                    // Send WA confirmation since it is now an active order
                    if (newOrder) {
                        if (!isCanje) await sendOrderConfirmationWhatsApp(newOrder.id, formData, paymentStatus, paymentStatus === 'pending_cash');
                    }

                    // 2. Delete from budgets
                    const { error: deleteError } = await supabase
                        .from('budgets')
                        .delete()
                        .eq('id', initialOrder.id);

                    if (deleteError) console.error('Error deleting budget after conversion:', deleteError);
                } else {
                    // Update in place (same table)
                    const table = initialOrder.status === 'presupuesto' ? 'budgets' : 'orders';
                    const isTnEdit = table === 'orders' && initialOrder.source === 'tiendanube';
                    const diff = isTnEdit ? computeOrderDiff(initialOrder, payload) : {};
                    const hasChanges = Object.keys(diff).length > 0;

                    const updatePayload: any = { ...payload, updated_at: new Date().toISOString() };
                    if (isTnEdit && hasChanges) {
                        updatePayload.edited_locally = true;
                        updatePayload.edited_at = new Date().toISOString();
                    }

                    const { error: updateError } = await supabase
                        .from(table)
                        .update(updatePayload)
                        .eq('id', initialOrder.id);
                    error = updateError;

                    if (!updateError && isTnEdit && hasChanges) {
                        const { data: sess } = await supabase.auth.getSession();
                        await logActivity(sess?.session?.user?.email, 'EDITAR_ORDEN_TN', {
                            order_id: initialOrder.id,
                            tiendanube_id: initialOrder.tiendanube_id,
                            contact_name: initialOrder.contact_name,
                            changes: diff,
                        });
                    }
                }
            } else {
                // Create new
                if (status === 'presupuesto') {
                    const { error: createError } = await supabase
                        .from('budgets')
                        .insert({
                            ...payload,
                            created_at: new Date().toISOString()
                        });
                    error = createError;
                } else {
                    const { data: createdOrder, error: createError } = await supabase
                        .from('orders')
                        .insert({
                            ...payload,
                            tiendanube_id: null,
                            created_at: new Date().toISOString()
                        })
                        .select()
                        .single();
                    error = createError;

                    // comprobante(s) de la transferencia → storage + receipt_urls (si falla, la orden ya está creada)
                    if (!createError && createdOrder && receiptFiles.length) {
                        try {
                            const urls: string[] = [];
                            for (const f of receiptFiles) {
                                const ext = ((f.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg');
                                const path = `manual_${createdOrder.id}_${Date.now()}_${urls.length}.${ext}`;
                                urls.push((await uploadFile('whatsapp-media', path, f, f.type || (ext === 'pdf' ? 'application/pdf' : 'image/jpeg')))!); // R2
                            }
                            await supabase.from('orders').update({ receipt_urls: urls }).eq('id', createdOrder.id);
                        } catch (e: any) {
                            showSnackbar('La orden se creó, pero no se pudo subir el comprobante: adjuntalo desde la card (' + (e?.message || e) + ')', 'error');
                        }
                    }

                    // Send order confirmation WhatsApp (not for MercadoLibre, only if phone is valid)
                    if (!createError && createdOrder && !isCanje) {
                        await sendOrderConfirmationWhatsApp(
                            createdOrder.id,
                            formData,
                            paymentStatus === 'paid' ? 'paid' : 'pending',
                            paymentStatus === 'pending_cash'
                        );
                    }
                }
            }

            if (error) throw error;

            // Cupón TN nuevo en esta venta → se descuenta un uso en Tiendanube
            if (status === 'nueva' && couponActive && coupon && coupon.code !== initialCoupon?.code) {
                const { data: cons } = await supabase.functions.invoke('tn-coupon', { body: { action: 'consume', code: coupon.code } });
                const { data: sess } = await supabase.auth.getSession();
                await logActivity(sess?.session?.user?.email, 'CUPON_TN_VENTA_MANUAL', { order_id: initialOrder?.id, contact_name: effectiveName,
                    note: `Cupón ${coupon.code} aplicado en venta manual (−$${Math.round(couponAmount).toLocaleString('es-AR')})${cons?.ok ? (cons.unlimited ? '' : ' · se descontó el uso en Tiendanube') : ' · NO se pudo descontar el uso en TN: ' + (cons?.error || '')}` });
                if (!cons?.ok) showSnackbar(`Ojo: no se pudo descontar el uso del cupón en Tiendanube (${cons?.error || 'error'})`, 'error');
            }

            showSnackbar(status === 'presupuesto' ? 'Presupuesto guardado' : 'Orden guardada correctamente', 'success');
            onSuccess();
        } catch (err: any) {
            console.error(err);
            showSnackbar(`Error al guardar: ${err.message}`, 'error');
        } finally {
            setSaving(false);
        }
    };

    // ─────────────────────────── UI ───────────────────────────
    // Rediseño 10/10/2026: 4 bloques (Cliente · Productos · Entrega · Pago) + resumen fijo a la
    // derecha con el total y las acciones. Cliente y catálogo se buscan EN EL MISMO CAMPO (antes
    // abrían otro popup encima). El estado de pago es Pendiente/Pagado: "pendiente en efectivo"
    // se deduce del medio de pago. Lo poco usado va en "Más opciones".
    const money = (n: number) => '$' + (Number(n) || 0).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    const isPaid = paymentStatus === 'paid';
    const showPmDiscount = !isBudgetEdit && !isAbsoluteDiscount && paymentMethod !== 'tarjeta';
    const hasAdvanced = !!(Number(formData.discount) || deposit || formData.is_circulo_rojo || sinPmDiscount || pmPctOverride != null || coupon);
    const filledItems = items.filter(i => i.name);
    const CTL = 'h-9 w-full min-w-0 rounded-[var(--radius-control)] border border-[var(--border-strong)] bg-card px-3 text-[length:var(--text-sm)] text-ink placeholder:text-faint focus:border-[var(--foreground)] focus:outline-none';
    const title = isCanje
        ? (initialOrder ? `Editar canje ${nextIdDisplay}` : 'Nuevo canje')
        : initialOrder
        ? `Editar ${isBudgetEdit ? 'presupuesto' : 'orden'} ${nextIdDisplay}`
        : 'Nueva venta';
    const subtitle = initialOrder?.created_at
        ? `Creada el ${new Date(initialOrder.created_at).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`
        : isCanje
        ? 'Pedido para un creador de contenido: entra al taller como cualquier pedido, pero no suma a facturación ni le manda WhatsApp.'
        : 'Cargá el cliente, los productos, la entrega y el pago. El total se calcula solo.';
    // Cerrar sin guardar: si ya cargaron algo, se confirma (antes un click afuera borraba todo).
    const isDirty = filledItems.length > 0 || !!formData.contact_name.trim();
    const requestClose = () => {
        if (!initialOrder && isDirty && !window.confirm('¿Cerrar sin guardar? Se pierde lo cargado.')) return;
        onCancel();
    };

    return (
        <Dialog open onOpenChange={o => { if (!o) requestClose(); }}>
            <DialogContent title={title} description={subtitle}
                className="!w-[min(calc(100vw_-_1.5rem),1140px)]"
                onPointerDownOutside={e => e.preventDefault()}
                onEscapeKeyDown={e => {
                    e.preventDefault();
                    // Escape cierra primero las listas desplegables; recién después el popup
                    if (showProductModal || showCustomerSearch) { setShowProductModal(false); setShowCustomerSearch(false); return; }
                    requestClose();
                }}>
                <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
                    <div className="grid min-w-0 gap-4">

                        {/* ── 1. Cliente ── */}
                        <Section n={1} title={isCanje ? 'Creador' : 'Cliente'} right={isCanje ? undefined :
                            <Button variant={formData.source === 'showroom' ? 'primary' : 'ghost'} size="sm" type="button"
                                title="Venta en el local sin cargar cliente"
                                onClick={() => {
                                    setSelectedCustomer(null);
                                    setFormData(prev => ({ ...prev, source: 'showroom', delivery_type: 'retiro', contact_name: prev.contact_name?.trim() ? prev.contact_name : 'Showroom' }));
                                }}>
                                <Store size={14} /> Venta en showroom
                            </Button>
                        }>
                            <div className="grid gap-4 sm:grid-cols-2">
                                {/* Nombre + búsqueda en la base de clientes en el mismo campo */}
                                <div className="relative">
                                    <Input label="Nombre" autoFocus placeholder="Escribí el nombre o buscá un cliente" autoComplete="off"
                                        value={formData.contact_name}
                                        onChange={e => {
                                            const v = e.target.value;
                                            setFormData(fd => ({ ...fd, contact_name: v }));
                                            if (selectedCustomer && v !== selectedCustomer.name) setSelectedCustomer(null);
                                            setCustomerSearchQuery(v);
                                            setShowCustomerSearch(v.trim().length >= 2);
                                        }}
                                        onBlur={() => setTimeout(() => setShowCustomerSearch(false), 150)} />
                                    {showCustomerSearch && (customerSearching || customerSearchResults.length > 0) && (
                                        <div className="absolute left-0 right-0 z-30 mt-1 max-h-72 overflow-y-auto rounded-[var(--radius-panel)] border border-line bg-card p-1 shadow-[var(--shadow-floating)]">
                                            <div className="px-2 py-1.5 text-[length:var(--text-xs)] text-faint">
                                                {customerSearching ? 'Buscando clientes…' : `${customerSearchResults.length} cliente(s) en la base — elegí uno o seguí escribiendo`}
                                            </div>
                                            {customerSearchResults.map(c => (
                                                <button key={c.id} type="button" onMouseDown={e => e.preventDefault()} onClick={() => pickCustomer(c)}
                                                    className="flex w-full items-center gap-2 rounded-[calc(var(--radius-control)-2px)] px-2 py-2 text-left hover:bg-muted">
                                                    {c.circulo_rojo && <Circle size={10} fill="#dc2626" color="#dc2626" className="shrink-0" />}
                                                    <span className="min-w-0 flex-1">
                                                        <span className="block truncate text-[length:var(--text-sm)] font-medium text-ink">{c.name}</span>
                                                        <span className="block truncate text-[length:var(--text-xs)] text-faint">
                                                            {[c.phone_display, `${c.order_count} pedido(s)`, c.last_order_at ? `última ${fmtDate(c.last_order_at)}` : null].filter(Boolean).join(' · ')}
                                                        </span>
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <Input label={isCanje ? 'Teléfono (solo para el envío)' : 'WhatsApp'} placeholder="11 2345 6789" inputMode="tel"
                                    value={formData.contact_phone}
                                    error={formData.contact_phone && !isValidPhone(formData.contact_phone) ? 'Número inválido. Ej: 11 2345 6789' : undefined}
                                    description={isCanje ? 'No se le manda ningún WhatsApp' : !formData.contact_phone ? 'Para mandarle la confirmación por WhatsApp' : undefined}
                                    onChange={e => setFormData(fd => ({ ...fd, contact_phone: e.target.value }))}
                                    onBlur={() => {
                                        if (formData.contact_phone) {
                                            const normalized = normalizePhone(formData.contact_phone);
                                            if (normalized) setFormData(prev => ({ ...prev, contact_phone: normalized }));
                                        }
                                    }} />
                            </div>
                            {selectedCustomer && (
                                <div className="mt-3 flex flex-wrap items-center gap-2">
                                    {selectedCustomer.circulo_rojo && <Badge tone="danger" size="sm">● Círculo Rojo</Badge>}
                                    <Badge size="sm">{selectedCustomer.order_count} pedido(s)</Badge>
                                    <Badge size="sm">Gastó {money(selectedCustomer.total_spent || 0)}</Badge>
                                    {selectedCustomer.last_order_at && <Badge size="sm">Última {fmtDate(selectedCustomer.last_order_at)}</Badge>}
                                    {selectedCustomer.coupon_code && <Badge size="sm" tone="info">{selectedCustomer.coupon_code}</Badge>}
                                    {selectedCustomer.notes && <span className="text-[length:var(--text-xs)] italic text-faint" title={selectedCustomer.notes}>📝 {selectedCustomer.notes.slice(0, 60)}</span>}
                                </div>
                            )}
                            {!isCanje && <div className="mt-4 flex flex-wrap items-center gap-3">
                                <span className="text-[length:var(--text-sm)] font-medium text-ink">¿Por dónde compró?</span>
                                {['tiendanube', 'mercadolibre', 'woocommerce'].includes(formData.source)
                                    // Orden de un canal: el origen no se cambia desde acá (pisaría el canal real)
                                    ? <Badge>{formData.source === 'tiendanube' ? 'Tiendanube' : formData.source === 'mercadolibre' ? 'MercadoLibre' : 'Boutique'}</Badge>
                                    : <SegmentedControl label="Origen" value={formData.source}
                                        onValueChange={v => setFormData(fd => ({ ...fd, source: v }))}
                                        options={[{ value: 'ig', label: 'Instagram' }, { value: 'whatsapp', label: 'WhatsApp' }, { value: 'showroom', label: 'Showroom' }]} />}
                            </div>}
                        </Section>

                        {/* ── 2. Productos ── */}
                        <Section n={2} title="Productos" right={
                            <div className="flex gap-1">
                                <Button variant="ghost" size="sm" type="button" onClick={() => setFrameForm(f => ({ ...f, open: !f.open }))} title="Marco Doble Panel Flotante con medida personalizada"><Frame size={14} /> Marco a medida</Button>
                                <Button variant="ghost" size="sm" type="button" onClick={() => { addItem(); setFocusPriceIdx(null); }} title="Producto que no está en el catálogo (a medida, especial…)"><Plus size={14} /> Fuera del catálogo</Button>
                            </div>
                        }>
                            {frameForm.open && (
                                <div className="mb-3 grid gap-2 rounded-[var(--radius-control)] border border-line bg-[var(--surface-muted)] p-3">
                                    <div className="text-[length:var(--text-xs)] font-medium uppercase tracking-wider text-faint">Marco a medida · medida de la lámina (se suman 2,5 cm de margen por lado)</div>
                                    <div className="flex flex-wrap items-end gap-2">
                                        <div className="w-24"><Input label="Ancho (cm)" inputMode="decimal" value={frameForm.w} onChange={e => setFrameForm(f => ({ ...f, w: e.target.value }))} /></div>
                                        <div className="w-24"><Input label="Alto (cm)" inputMode="decimal" value={frameForm.h} onChange={e => setFrameForm(f => ({ ...f, h: e.target.value }))}
                                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addFrame(); } }} /></div>
                                        <div className="w-20"><Input label="Cantidad" inputMode="numeric" value={frameForm.q} onChange={e => setFrameForm(f => ({ ...f, q: e.target.value.replace(/[^\d]/g, '') }))} /></div>
                                        <Button size="sm" type="button" disabled={!frameQuote} onClick={addFrame}><Plus size={14} /> Agregar</Button>
                                    </div>
                                    {frameQuote
                                        ? <div className="text-[length:var(--text-sm)] text-ink">Marco final <b>{frameQuote.fw}×{frameQuote.fh} cm</b> · <b className="tabular-nums">{money(frameQuote.price)}</b> c/u</div>
                                        : !frameModel && <div className="text-[length:var(--text-xs)] text-faint">Cargando precios del catálogo…</div>}
                                </div>
                            )}
                            {/* Buscador del catálogo inline (antes era otro popup) */}
                            <div className="relative">
                                <div className="flex h-10 items-center gap-2 rounded-[var(--radius-control)] border border-[var(--border-strong)] bg-card px-3 focus-within:border-[var(--foreground)]">
                                    <Search size={16} className="shrink-0 text-faint" />
                                    <input value={modalSearch} placeholder="Buscar producto del catálogo…" autoComplete="off"
                                        onChange={e => { setModalSearch(e.target.value); setShowProductModal(true); }}
                                        onFocus={() => setShowProductModal(true)}
                                        onBlur={() => setTimeout(() => setShowProductModal(false), 150)}
                                        onKeyDown={e => { if (e.key === 'Enter' && modalSearch.trim() && modalFilteredProducts.length === 0) { e.preventDefault(); addCustomItem(modalSearch); } }}
                                        className="h-full min-w-0 flex-1 bg-transparent text-[length:var(--text-sm)] text-ink placeholder:text-faint focus:outline-none" />
                                    {modalSearch && <button type="button" aria-label="Borrar búsqueda" onClick={() => setModalSearch('')} className="text-faint hover:text-ink"><X size={15} /></button>}
                                </div>
                                {showProductModal && (
                                    <div className="absolute left-0 right-0 z-30 mt-1 max-h-80 overflow-y-auto rounded-[var(--radius-panel)] border border-line bg-card p-1 shadow-[var(--shadow-floating)]"
                                        onMouseDown={e => e.preventDefault()}>
                                        <div className="sticky top-0 z-10 flex items-center justify-between bg-card px-2 py-1.5 text-[length:var(--text-xs)] text-faint">
                                            <span>{modalSearch.trim() ? `Resultados para “${modalSearch.trim()}”` : 'Catálogo'} · tocá para agregar</span>
                                            <button type="button" onClick={() => { setShowProductModal(false); setModalSearch(''); }}
                                                className="rounded-[var(--radius-control)] px-2 py-0.5 font-medium text-ink hover:bg-muted">Listo</button>
                                        </div>
                                        {modalSearch.trim() && (
                                            <button type="button" onClick={() => addCustomItem(modalSearch)}
                                                className="mb-1 flex w-full items-center gap-2 rounded-[var(--radius-control)] border border-dashed border-[var(--border-strong)] px-3 py-2 text-left text-[length:var(--text-sm)] text-ink hover:bg-muted">
                                                <Plus size={15} className="shrink-0" />
                                                <span className="min-w-0 truncate">Agregar <b>“{modalSearch.trim()}”</b> como producto fuera del catálogo</span>
                                                {modalFilteredProducts.length === 0 && <span className="ml-auto shrink-0 text-[length:var(--text-xs)] text-faint">Enter</span>}
                                            </button>
                                        )}
                                        {modalFilteredProducts.length === 0 && !modalSearch.trim() && <div className="px-3 py-6 text-center text-[length:var(--text-sm)] text-faint">No hay productos que coincidan.</div>}
                                        {modalFilteredProducts.map(p => {
                                            const pvs = getVariantsForProduct(p.id);
                                            const hasVariants = pvs.length > 1;
                                            const open = expandedProductId === p.id;
                                            const pct = promoPctForProduct(p);
                                            // la promo manual no se aplica sobre un precio que ya es promocional en TN
                                            const priceOf = (base: number, v?: any) => (pct > 0 && !(v?.promotional_price && Number(v.promotional_price) < Number(v.price)) ? Math.round(base * (1 - pct / 100) * 100) / 100 : base);
                                            return (
                                                <div key={p.id}>
                                                    <button type="button"
                                                        onClick={() => hasVariants ? setExpandedProductId(open ? null : p.id) : (addProductToOrder(p), setModalSearch(''), setShowProductModal(false))}
                                                        className={`flex w-full items-center gap-3 rounded-[calc(var(--radius-control)-2px)] px-2 py-2 text-left hover:bg-muted ${open ? 'bg-muted' : ''}`}>
                                                        <Thumb src={p.image_url} name={p.name} size="sm" />
                                                        <span className="min-w-0 flex-1">
                                                            <span className="flex items-center gap-1.5 truncate text-[length:var(--text-sm)] font-medium text-ink">
                                                                <span className="truncate">{p.name}</span>
                                                                {pct > 0 && <Badge size="sm" tone="danger">-{pct}%</Badge>}
                                                            </span>
                                                            <span className="block text-[length:var(--text-xs)] text-faint">
                                                                {hasVariants ? `${pvs.length} variantes — elegí una` : pvs[0] ? money(priceOf(pvs[0].promotional_price || pvs[0].price || 0, pvs[0])) : 'Sin precio'}
                                                            </span>
                                                        </span>
                                                        {hasVariants
                                                            ? <ChevronDown size={16} className={`shrink-0 text-faint transition-transform ${open ? 'rotate-180' : ''}`} />
                                                            : <Plus size={16} className="shrink-0 text-faint" />}
                                                    </button>
                                                    {open && hasVariants && (
                                                        <div className="mb-1 ml-11 grid gap-0.5 border-l border-line pl-2">
                                                            {pvs.map(v => (
                                                                <button key={v.id} type="button" onClick={() => addProductToOrder(p, v)}
                                                                    className="flex items-center justify-between gap-3 rounded-[calc(var(--radius-control)-2px)] px-2 py-1.5 text-left text-[length:var(--text-sm)] hover:bg-muted">
                                                                    <span className="min-w-0 truncate text-ink">{v.values.length ? v.values.join(' · ') : 'Estándar'}</span>
                                                                    <span className="flex shrink-0 items-center gap-2 tabular-nums text-soft">{money(priceOf(v.promotional_price || v.price || 0, v))}<Plus size={14} /></span>
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* Ítems */}
                            {items.length === 0 ? (
                                <div className="mt-3 rounded-[var(--radius-control)] border border-dashed border-line px-4 py-6 text-center text-[length:var(--text-sm)] text-faint">
                                    Buscá un producto arriba o agregá un ítem manual.
                                </div>
                            ) : (
                                <ul className="m-0 mt-3 grid list-none gap-2 p-0">
                                    {items.map((item, i) => (
                                        <li key={i} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 rounded-[var(--radius-control)] border border-line p-2">
                                            <input value={item.name} placeholder="Nombre del ítem" onChange={e => handleItemChange(i, 'name', e.target.value)}
                                                className={cn(CTL, 'border-transparent font-medium hover:border-line')} />
                                            <button type="button" aria-label="Quitar" title="Quitar" onClick={() => removeItem(i)}
                                                className="grid h-9 w-9 place-items-center rounded-[var(--radius-control)] text-faint hover:bg-muted hover:text-[var(--danger)]"><Trash2 size={15} /></button>
                                            <div className="col-span-2 flex flex-wrap items-center gap-3 pl-1">
                                                <div className="flex items-center gap-1">
                                                    <button type="button" aria-label="Uno menos" disabled={item.quantity <= 1}
                                                        onClick={() => handleItemChange(i, 'quantity', Math.max(1, item.quantity - 1))}
                                                        className="grid h-9 w-8 place-items-center rounded-[var(--radius-control)] border border-line text-soft hover:text-ink disabled:opacity-40">−</button>
                                                    <input type="number" min={1} value={item.quantity} aria-label="Cantidad"
                                                        onChange={e => handleItemChange(i, 'quantity', parseInt(e.target.value) || 1)}
                                                        className={cn(CTL, 'w-12 px-1 text-center tabular-nums')} />
                                                    <button type="button" aria-label="Uno más" onClick={() => handleItemChange(i, 'quantity', item.quantity + 1)}
                                                        className="grid h-9 w-8 place-items-center rounded-[var(--radius-control)] border border-line text-soft hover:text-ink">+</button>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <span className="text-[length:var(--text-xs)] text-faint">$</span>
                                                    <input type="number" min={0} step="0.01" value={item.price || ''} placeholder="Precio" aria-label="Precio unitario"
                                                        autoFocus={focusPriceIdx === i}
                                                        onChange={e => handleItemChange(i, 'price', parseFloat(e.target.value) || 0)}
                                                        className={cn(CTL, 'w-28 text-right tabular-nums')} />
                                                </div>
                                                <span className="ml-auto text-right text-[length:var(--text-sm)] font-semibold tabular-nums text-ink">{money(item.price * item.quantity)}</span>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </Section>

                        {/* ── 3. Entrega ── */}
                        <Section n={3} title="Entrega">
                            <SegmentedControl label="Tipo de entrega" value={formData.delivery_type}
                                onValueChange={v => {
                                    setFormData(fd => ({ ...fd, delivery_type: v, ...(v === 'retiro' ? { shipping_cost: 0 } : {}) }));
                                    if (v === 'retiro') setFreeShipping(false);
                                }}
                                options={[{ value: 'retiro', label: 'Retira en el showroom' }, { value: 'envio', label: 'Envío a domicilio' }]} />

                            {formData.delivery_type === 'envio' && (
                                <div className="mt-4 grid gap-4">
                                    <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_140px]">
                                        <div className="min-w-0">
                                            <span className="mb-2 block text-[length:var(--text-sm)] font-medium text-ink">Dirección</span>
                                            {/* Calle + altura: elegí la sugerencia de Google (trae la ubicación exacta para Cabify) */}
                                            <AddressField value={formData.delivery_address}
                                                onChange={v => setFormData(fd => ({ ...fd, delivery_address: v }))}
                                                onCoords={c => setDeliveryCoords(c)}
                                                onPlace={onAddressPlace}
                                                placeholder="Calle 1234, Localidad" />
                                        </div>
                                        <label className="min-w-0">
                                            <span className="mb-2 block text-[length:var(--text-sm)] font-medium text-ink">Piso / Depto</span>
                                            {/* mismo alto que el campo de dirección para que queden alineados */}
                                            <input placeholder="ej: 4B" value={formData.delivery_unit}
                                                onChange={e => setFormData(fd => ({ ...fd, delivery_unit: e.target.value }))}
                                                className="h-[var(--control-height-md)] w-full min-w-0 rounded-[var(--radius-control)] border border-[var(--border-strong)] bg-card px-3 text-[length:var(--text-sm)] text-ink placeholder:text-faint transition-colors hover:border-[var(--foreground)] focus:border-[var(--foreground)] focus:outline-none" />
                                        </label>
                                    </div>
                                    {/* canje: no se le cobra envío al creador */}
                                    {!isCanje && <div>
                                        <span className="mb-2 block text-[length:var(--text-sm)] font-medium text-ink">Costo de envío para el cliente</span>
                                        <div className="flex flex-wrap items-center gap-2">
                                            {([{ label: 'Gratis', value: -1 }, { label: 'CABA', value: 6800 }, { label: 'AMBA', value: 12500 }, { label: 'AMBA lejos', value: 14000 }]).map(o => {
                                                const on = o.value === -1 ? freeShipping : (!freeShipping && Number(formData.shipping_cost) === o.value);
                                                return (
                                                    <button key={o.label} type="button"
                                                        onClick={() => {
                                                            setShippingTouched(true);
                                                            if (o.value === -1) { setFreeShipping(!freeShipping); setFormData(fd => ({ ...fd, shipping_cost: 0 })); }
                                                            else { setFreeShipping(false); setFormData(fd => ({ ...fd, shipping_cost: o.value })); }
                                                        }}
                                                        className={`h-9 rounded-[var(--radius-pill)] border px-3 text-[length:var(--text-sm)] transition-colors ${on ? 'border-[var(--foreground)] bg-[var(--foreground)] text-[var(--card)]' : 'border-line text-soft hover:text-ink'}`}>
                                                        {o.label}{o.value > 0 && <span className="ml-1 opacity-70">{money(o.value)}</span>}
                                                    </button>
                                                );
                                            })}
                                            <div className="flex items-center gap-1">
                                                <span className="whitespace-nowrap text-[length:var(--text-xs)] text-faint">Otro $</span>
                                                <input type="text" inputMode="numeric" placeholder="0" aria-label="Costo de envío manual"
                                                    value={formData.shipping_cost && ![6800, 12500, 14000].includes(Number(formData.shipping_cost)) ? Number(formData.shipping_cost).toLocaleString('es-AR') : ''}
                                                    onChange={e => {
                                                        const digits = e.target.value.replace(/[^\d]/g, '');
                                                        setShippingTouched(true);
                                                        if (digits) setFreeShipping(false);
                                                        setFormData(fd => ({ ...fd, shipping_cost: digits ? parseInt(digits, 10) : 0 }));
                                                    }}
                                                    className={cn(CTL, 'w-24 text-right tabular-nums')} />
                                            </div>
                                        </div>
                                        {shipZone && (
                                            <p className="mt-2 text-[length:var(--text-xs)] text-faint">
                                                Zona: <b className="font-semibold text-soft">{shipZone.partido || ZONE_LABEL[shipZone.zone]}</b> · {ZONE_LABEL[shipZone.zone]}
                                                {shipZone.zone === 'fuera'
                                                    ? ' — no hay tarifa propia: elegí el costo a mano o mandalo por otro canal.'
                                                    : shippingTouched ? ' — dejé el costo que elegiste.' : ` — preseleccioné ${ZONE_LABEL[shipZone.zone]} (${money(ZONE_PRICE[shipZone.zone])}).`}
                                            </p>
                                        )}
                                    </div>}
                                </div>
                            )}

                            {/* Fecha de LISTO = empaquetado. Ordena la cola del taller. */}
                            <div className="mt-5 grid gap-3 border-t border-line pt-4 sm:grid-cols-[180px_minmax(0,1fr)] sm:items-end">
                                <Input label="Listo para (empaquetado)" type="date" value={readyBy} required
                                    onChange={e => { setReadyBy(e.target.value); setReadyTouched(true); }} />
                                <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
                                    <Checkbox label="Comprometida con el cliente" checked={readyPromised}
                                        onCheckedChange={c => { setReadyPromised(c === true); setReadyTouched(true); }} />
                                    <Checkbox label="Urgente" checked={isUrgent} onCheckedChange={c => setIsUrgent(c === true)} />
                                </div>
                            </div>
                            <p className="mt-2 text-[length:var(--text-xs)] text-faint">
                                {readySuggestion.slow
                                    ? <>Sugerida: <b className="font-semibold text-soft">{readySuggestion.iso.split('-').reverse().join('/')}</b> · {readySuggestion.days === 0 ? 'todo en stock' : `${readySuggestion.days} días por “${readySuggestion.slow.slice(0, 40)}”`}</>
                                    : <>Se calcula sola al agregar productos ({defaultLeadDays} días por defecto, 0 si está en stock).</>}
                                {readyTouched && readyBy !== readySuggestion.iso && readySuggestion.slow && (
                                    <> · <button type="button" className="text-ink underline" onClick={() => { setReadyBy(readySuggestion.iso); setReadyTouched(false); setReadyPromised(false); }}>usar la sugerida</button></>
                                )}
                            </p>

                            <div className="mt-4">
                                <Textarea label="Notas del pedido" placeholder="Detalles, pedidos especiales, aclaraciones…" rows={2}
                                    value={formData.note} onChange={e => setFormData(fd => ({ ...fd, note: e.target.value }))} />
                            </div>
                        </Section>

                        {/* ── 4. Pago ── (un canje no se cobra). Al editar un PRESUPUESTO solo se muestran los
                            descuentos (extra % o $ y Círculo Rojo): medio/estado de pago, seña y cupón
                            se definen al pasarlo a orden (02/11/2026; antes no se podía descontar). */}
                        {!isCanje && (
                            <Section n={4} title={isBudgetEdit ? 'Descuento' : 'Pago'}>
                                {!isBudgetEdit && (
                                <div className="grid gap-4 sm:grid-cols-2">
                                    <div>
                                        <span className="mb-2 block text-[length:var(--text-sm)] font-medium text-ink">Medio de pago</span>
                                        <SegmentedControl label="Medio de pago" value={paymentMethod}
                                            onValueChange={v => { setPaymentMethod(v as any); setPmPctOverride(null); }}
                                            options={[
                                                { value: 'efectivo', label: isAbsoluteDiscount ? 'Efectivo' : 'Efectivo −15%' },
                                                { value: 'transferencia', label: isAbsoluteDiscount ? 'Transferencia' : 'Transf. −15%' },
                                                { value: 'tarjeta', label: 'Tarjeta' },
                                            ]} />
                                    </div>
                                    {showTransferAccount && (
                                        <div className="sm:col-span-2 sm:order-last">
                                            <span className={`mb-2 block text-[length:var(--text-sm)] font-medium ${transferAccountError && !transferAccount ? 'text-[var(--danger)]' : 'text-ink'}`}>
                                                ¿A qué cuenta transfiere? {needsTransferAccount ? '*' : <span className="font-normal text-soft">(si no elegís, se busca en la financiera)</span>}
                                            </span>
                                            <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Cuenta de la transferencia">
                                                {([['financiera', 'Financiera', 'Quinta C · Alias Quinta.GT'], ['krilic', 'Krilic', 'cuenta propia']] as const).map(([v, label, sub]) => (
                                                    <button key={v} type="button" role="radio" aria-checked={transferAccount === v}
                                                        onClick={() => { setTransferAccount(v); setTransferAccountError(false); }}
                                                        className={`grid min-w-[150px] flex-1 gap-0.5 rounded-[var(--radius-control)] border px-3 py-2 text-left transition-colors ${transferAccount === v
                                                            ? 'border-[var(--foreground)] bg-muted outline outline-2 outline-[var(--foreground)]'
                                                            : transferAccountError ? 'border-[var(--danger)] bg-card' : 'border-line bg-card hover:bg-muted'}`}>
                                                        <span className="text-[length:var(--text-sm)] font-semibold text-ink">{v === 'financiera' ? '1 · ' : '2 · '}{label}</span>
                                                        <span className="text-[length:var(--text-xs)] text-soft">{sub}</span>
                                                    </button>
                                                ))}
                                            </div>
                                            {transferAccountError && !transferAccount && <p className="mt-1.5 text-[length:var(--text-xs)] text-[var(--danger)]">Obligatorio: define si hay que buscarla en el estado de cuenta de la financiera.</p>}
                                            {!initialOrder && needsTransferAccount && (
                                                <div className="mt-4">
                                                    <span className={`mb-2 block text-[length:var(--text-sm)] font-medium ${receiptError && isPaid && !receiptFiles.length ? 'text-[var(--danger)]' : 'text-ink'}`}>
                                                        Comprobante de la transferencia {isPaid ? '*' : <span className="font-normal text-soft">(opcional mientras esté pendiente)</span>}
                                                    </span>
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        {receiptFiles.map((f, i) => (
                                                            <span key={i} className="inline-flex max-w-[260px] items-center gap-1.5 rounded-[var(--radius-control)] border border-line bg-muted px-2 py-1 text-[length:var(--text-xs)] text-ink">
                                                                <span className="truncate" title={f.name}>📎 {f.name}</span>
                                                                <button type="button" aria-label={`Quitar ${f.name}`} className="text-soft hover:text-ink"
                                                                    onClick={() => setReceiptFiles(fs => fs.filter((_, j) => j !== i))}>✕</button>
                                                            </span>
                                                        ))}
                                                        <label className={`inline-flex cursor-pointer items-center gap-1.5 rounded-[var(--radius-control)] border border-dashed px-3 py-1.5 text-[length:var(--text-sm)] ${receiptError && isPaid && !receiptFiles.length ? 'border-[var(--danger)] text-[var(--danger)]' : 'border-[var(--border-strong)] text-ink hover:bg-muted'}`}>
                                                            📎 {receiptFiles.length ? 'Agregar otro' : 'Adjuntar comprobante'}
                                                            <input type="file" accept="image/*,application/pdf,.pdf" multiple className="hidden"
                                                                onChange={e => { const fl = Array.from(e.target.files || []); if (fl.length) { setReceiptFiles(fs => [...fs, ...fl]); setReceiptError(false); } e.target.value = ''; }} />
                                                        </label>
                                                    </div>
                                                    {receiptError && isPaid && !receiptFiles.length && <p className="mt-1.5 text-[length:var(--text-xs)] text-[var(--danger)]">Obligatorio si está pagada: sin comprobante no se puede controlar que la plata llegó.</p>}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <div>
                                        <span className="mb-2 block text-[length:var(--text-sm)] font-medium text-ink">Estado del pago</span>
                                        <SegmentedControl label="Estado del pago" value={isPaid ? 'paid' : 'pending'}
                                            onValueChange={v => setPaymentStatus(v === 'paid' ? 'paid' : (paymentMethod === 'efectivo' ? 'pending_cash' : 'pending'))}
                                            options={[{ value: 'pending', label: paymentMethod === 'efectivo' ? 'Paga al retirar' : 'Pendiente' }, { value: 'paid', label: 'Pagado' }]} />
                                    </div>
                                </div>

                                )}

                                <details className={cn('group rounded-[var(--radius-control)] border border-line', !isBudgetEdit && 'mt-4')} open={hasAdvanced || isBudgetEdit || undefined}>
                                    <summary className="flex cursor-pointer list-none items-center justify-between px-3 py-2.5 text-[length:var(--text-sm)] font-medium text-ink">
                                        Más opciones <span className="text-[length:var(--text-xs)] font-normal text-faint">descuentos, seña, Círculo Rojo</span>
                                        <ChevronDown size={15} className="text-faint transition-transform group-open:rotate-180" />
                                    </summary>
                                    <div className="grid gap-4 border-t border-line p-3">
                                        <Checkbox label="Círculo Rojo (10% off)" description="Se aplica como descuento extra del 10%"
                                            checked={formData.is_circulo_rojo}
                                            onCheckedChange={c => {
                                                const checked = c === true;
                                                setFormData(prev => ({
                                                    ...prev, is_circulo_rojo: checked,
                                                    // Círculo Rojo es un 10%: si el extra estaba en $, pasa a %
                                                    ...(checked && prev.discount_type === 'fixed' ? { discount_type: 'pct' as const, discount: 10 } : {
                                                    discount: checked ? Math.max(prev.discount || 0, 10) : (prev.discount === 10 ? 0 : prev.discount) }),
                                                }));
                                            }} />
                                        {!isAbsoluteDiscount && !isCanje && !isBudgetEdit && (
                                            <div className="grid gap-1.5">
                                                {coupon ? (
                                                    <div className="flex flex-wrap items-center gap-2 text-[length:var(--text-sm)]">
                                                        <Badge tone="success">🎟️ {coupon.code}</Badge>
                                                        <span className="text-soft">{coupon.type === 'percentage' ? `${coupon.value}% off` : coupon.type === 'absolute' ? `$${coupon.value.toLocaleString('es-AR')} off` : 'envío gratis'}{coupon.products?.length ? ' (solo algunos productos)' : ''}{couponAmount > 0 ? ` · −${money(couponAmount)}` : ''}</span>
                                                        <Button variant="ghost" size="sm" type="button" onClick={() => { setCoupon(null); setCouponMsg(null); }}>Quitar</Button>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-end gap-2">
                                                        <div className="w-56">
                                                            <Input label="Cupón de Tiendanube" placeholder="KRI-…, VOLVE-…" value={couponInput}
                                                                onChange={e => setCouponInput(e.target.value.toUpperCase())}
                                                                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); (document.getElementById('apply-coupon') as HTMLButtonElement)?.click(); } }} />
                                                        </div>
                                                        <Button id="apply-coupon" variant="secondary" size="sm" type="button" disabled={!couponInput.trim() || couponChecking}
                                                            onClick={async () => {
                                                                setCouponChecking(true); setCouponMsg(null);
                                                                const { data, error } = await supabase.functions.invoke('tn-coupon', { body: { action: 'validate', code: couponInput.trim(), subtotal } });
                                                                setCouponChecking(false);
                                                                if (error || !data?.ok) { setCouponMsg({ tone: 'err', text: data?.error || error?.message || 'No se pudo validar' }); return; }
                                                                const c = data.coupon;
                                                                setCoupon({ code: c.code, type: c.type, value: Number(c.value) || 0, products: c.products || [] });
                                                                setCouponInput('');
                                                                setCouponMsg({ tone: 'ok', text: `Cupón válido${c.remaining != null ? ` · le quedan ${c.remaining} uso(s)` : ''}${c.end_date ? ` · vence ${String(c.end_date).slice(0, 10)}` : ''}${c.first_consumer_purchase ? ' · ojo: es solo para primera compra' : ''}. Se descuenta el uso al guardar la orden.` });
                                                            }}>
                                                            {couponChecking ? 'Validando…' : 'Aplicar'}
                                                        </Button>
                                                    </div>
                                                )}
                                                {couponMsg && <p className={couponMsg.tone === 'err' ? 'text-[length:var(--text-xs)] text-[var(--danger)]' : 'text-[length:var(--text-xs)] text-[var(--success)]'}>{couponMsg.text}</p>}
                                            </div>
                                        )}
                                        <div className="grid gap-4 sm:grid-cols-2">
                                            {isAbsoluteDiscount ? (
                                                <p className="text-[length:var(--text-xs)] text-faint">Orden de Tiendanube: el descuento ({money(Number(formData.discount) || 0)}) viene fijo de la tienda.</p>
                                            ) : (
                                                <div className="flex items-end gap-2">
                                                    <div className="min-w-0 flex-1">
                                                        {formData.discount_type === 'fixed' ? (
                                                            <Input label="Descuento extra ($)" inputMode="numeric" placeholder="0"
                                                                value={formData.discount ? Number(formData.discount).toLocaleString('es-AR') : ''}
                                                                onChange={e => { const d = e.target.value.replace(/[^\d]/g, ''); setFormData(fd => ({ ...fd, discount: d ? parseInt(d, 10) : 0 })); }} />
                                                        ) : (
                                                            <Input label="Descuento extra (%)" type="number" min={0} max={100}
                                                                value={formData.discount || ''} placeholder="0"
                                                                onChange={e => setFormData(fd => ({ ...fd, discount: Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)) }))} />
                                                        )}
                                                    </div>
                                                    {/* % o monto fijo: al cambiar de tipo se vacía el valor (un 10 no significa lo mismo) */}
                                                    <div className="flex shrink-0 overflow-hidden rounded-[var(--radius-control)] border border-line" role="group" aria-label="Tipo de descuento">
                                                        {(['pct', 'fixed'] as const).map(t => (
                                                            <button key={t} type="button" aria-pressed={formData.discount_type === t}
                                                                onClick={() => setFormData(fd => fd.discount_type === t ? fd : ({ ...fd, discount_type: t, discount: 0, is_circulo_rojo: t === 'fixed' ? false : fd.is_circulo_rojo }))}
                                                                className={'h-9 w-9 text-[length:var(--text-sm)] font-semibold ' + (formData.discount_type === t ? 'bg-[var(--foreground)] text-[var(--card)]' : 'bg-card text-soft hover:bg-muted')}>
                                                                {t === 'pct' ? '%' : '$'}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            {!isPaid && !isBudgetEdit && (
                                                <Input label="Seña ya cobrada ($)" inputMode="numeric" placeholder="0"
                                                    value={deposit ? deposit.toLocaleString('es-AR') : ''}
                                                    onChange={e => { const d = e.target.value.replace(/[^\d]/g, ''); setDeposit(d ? parseInt(d, 10) : 0); }} />
                                            )}
                                        </div>
                                        {showPmDiscount && (
                                            <div className="flex flex-wrap items-end gap-4">
                                                <div className="w-40">
                                                    <Input label={`Descuento ${paymentMethod} (%)`} type="number" min={0} max={100}
                                                        disabled={sinPmDiscount} value={paymentMethodDiscountPct}
                                                        onChange={e => setPmPctOverride(Math.max(0, Math.min(100, Number(e.target.value) || 0)))} />
                                                </div>
                                                <Checkbox label="Sin descuento por medio de pago" description="Precio ya negociado, mayorista, etc."
                                                    checked={sinPmDiscount} onCheckedChange={c => setSinPmDiscount(c === true)} />
                                            </div>
                                        )}
                                    </div>
                                </details>
                            </Section>
                        )}
                    </div>

                    {/* ── Resumen (fijo) ── */}
                    <aside className="min-w-0 lg:sticky lg:top-0">
                        <div className="rounded-[var(--radius-surface)] border border-line bg-card p-4 sm:p-5">
                            <h3 className="mb-2 text-[length:var(--text-base)] font-semibold text-ink">Resumen</h3>
                            {filledItems.length === 0
                                ? <p className="py-2 text-[length:var(--text-sm)] text-faint">Todavía no hay productos.</p>
                                : <ul className="m-0 mb-2 flex list-none flex-col gap-1 p-0">
                                    {filledItems.slice(0, 6).map((it, i) => (
                                        <li key={i} className="flex items-baseline justify-between gap-2 text-[length:var(--text-xs)] text-soft">
                                            <span className="min-w-0 truncate">{it.quantity}× {it.name}</span>
                                            <span className="shrink-0 tabular-nums">{money(it.price * it.quantity)}</span>
                                        </li>
                                    ))}
                                    {filledItems.length > 6 && <li className="text-[length:var(--text-xs)] text-faint">+{filledItems.length - 6} más</li>}
                                </ul>}
                            <div className="border-t border-line pt-2">
                                {isCanje
                                    ? <SumRow label="Valor de lista (no se cobra)" value={<span className="text-faint line-through">{money(subtotal)}</span>} />
                                    : <SumRow label="Subtotal" value={money(subtotal)} />}
                                {promoDiscountTotal > 0 && <SumRow tone="ok" label="Promo de catálogo (incluida)" value={`−${money(promoDiscountTotal)}`} />}
                                {pmDiscountAmount > 0 && <SumRow tone="ok" label={`Descuento ${paymentMethod} (${paymentMethodDiscountPct}%)`} value={`−${money(pmDiscountAmount)}`} />}
                                {discountAmount > 0 && <SumRow tone="ok" label={isAbsoluteDiscount ? 'Descuento (Tiendanube)' : formData.discount_type === 'fixed' ? 'Descuento extra' : `Descuento extra (${formData.discount}%)${formData.is_circulo_rojo ? ' · Círculo Rojo' : ''}`} value={`−${money(discountAmount)}`} />}
                                {couponAmount > 0 && <SumRow tone="ok" label={`Cupón ${coupon?.code}`} value={`−${money(couponAmount)}`} />}
                                {formData.delivery_type === 'envio' && (freeShipping
                                    ? <SumRow tone="ok" label="Envío" value="GRATIS" />
                                    : shippingCost > 0 ? <SumRow label="Envío" value={`+${money(shippingCost)}`} /> : null)}
                            </div>
                            <div className="mt-2 flex items-baseline justify-between border-t border-line pt-3">
                                <span className="text-[length:var(--text-sm)] font-medium text-ink">{isCanje ? 'Total (canje)' : 'Total'}</span>
                                <span className="text-[length:var(--text-2xl)] font-semibold tabular-nums tracking-[var(--tracking-display)] text-ink">{money(isCanje ? 0 : grandTotal)}</span>
                            </div>
                            {isCanje && <p className="mt-1 text-[length:var(--text-xs)] text-faint">🎁 Canje: no suma a facturación ni manda WhatsApp.</p>}
                            {!isPaid && deposit > 0 && !isBudgetEdit && (
                                <div className="mt-1">
                                    <SumRow label="Seña cobrada" value={`−${money(deposit)}`} />
                                    <SumRow tone="ok" label={<b>Resta cobrar</b>} value={<b>{money(Math.max(0, grandTotal - deposit))}</b>} />
                                </div>
                            )}
                            {!initialOrder && !isBudgetEdit && paymentMethod !== 'tarjeta' && pmDiscountAmount > 0 && (
                                <p className="mt-2 text-[length:var(--text-xs)] text-faint">En un presupuesto se guarda el precio de lista; el descuento por {paymentMethod} aplica al crear la orden.</p>
                            )}

                            <div className="mt-4 grid gap-2">
                                {initialOrder ? (
                                    <Button loading={saving} onClick={() => handleSubmit(isBudgetEdit ? 'presupuesto' : 'nueva')}>
                                        <Save size={16} /> Guardar cambios
                                    </Button>
                                ) : (<>
                                    <Button loading={saving} onClick={() => handleSubmit('nueva')}><ShoppingBag size={16} /> {isCanje ? 'Crear canje' : 'Crear orden'}</Button>
                                    {!isCanje && <Button variant="secondary" disabled={saving} onClick={() => handleSubmit('presupuesto')}><FileText size={16} /> Guardar como presupuesto</Button>}
                                </>)}
                                <Button variant="ghost" type="button" onClick={requestClose}>Cancelar</Button>
                            </div>
                        </div>
                    </aside>
                </div>
            </DialogContent>
        </Dialog>
    );
}
